import React, { Component } from "react";
import { classes, style } from "typestyle";
import moment from "moment";
import Throbber from "../../components/Throbber";
import Alert from "../../components/Alert";
import CenterLayout from "../../components/CenterLayout";
import ListView, {
  LIST_ROW_COLOR_INFO,
  LIST_ROW_COLOR_ERROR,
} from "../../components/ListView";

import {
  getWorkflowsList,
  getWorkflowsPending,
  getWorkflowsError,
} from "../../../redux/modules/workflow/reducer";

import { WorkflowsState } from "../../../redux/modules/workflow/reducer";
import {
  WORKFLOW_STATUS_RUNNING,
  WORKFLOW_STATUS_SUCCESSFUL,
  WORKFLOW_STATUS_WAITING_DATA,
  WORKFLOW_STATUS_FAILED,
  CS_STATUS_PUBLISHED_MESSAGE_SENT,
  CS_STATUS_PUBLISHED,
  WORKFLOW_STATUS_WAITING,
} from "../../../redux/modules/workflow/constants";

import type { Workflow } from "../../../redux/modules/workflow/reducer";
import { Blood, Gold, ListItemText, Mint, regularStyle } from "@traceair/tds";

interface Props {
  workflowsState: WorkflowsState;
  onWorkflowClick: (wf: Workflow) => void;
  isModerator: boolean;
}

export default class WorkflowList extends Component<Props> {
  render() {
    const { workflowsState, onWorkflowClick } = this.props;
    const error = getWorkflowsError(workflowsState);
    const pending = getWorkflowsPending(workflowsState);
    const entries = getWorkflowsList(workflowsState);

    if (pending) {
      return (
        <CenterLayout className={style({ marginTop: "12px" })}>
          <Throbber />
        </CenterLayout>
      );
    }

    if (error) {
      return (
        <Alert>{error.message ? error.message : "Something went wrong"}</Alert>
      );
    }

    return (
      <ListView
        leftColumnHeader={null}
        rightColumnHeader="Status"
        rows={entries}
        renderLeftColumn={this.renderLeftColumn}
        renderRightColumn={this.renderRightColumn}
        onRowClick={onWorkflowClick}
        renderRowColor={this.renderRowColor}
        onSort={(rows) =>
          rows.sort((a, b) => {
            const left = a.creationTimeUtc
              ? a.creationTimeUtc.toLowerCase()
              : "";
            const right = b.creationTimeUtc
              ? b.creationTimeUtc.toLowerCase()
              : "";
            if (left === right) {
              return 0;
            }

            return left < right ? 1 : -1;
          })
        }
      />
    );
  }

  renderRowColor = (workflow: Workflow) => {
    const { status } = workflow;
    if (status === WORKFLOW_STATUS_WAITING_DATA) {
      return LIST_ROW_COLOR_INFO;
    }
    if (status === WORKFLOW_STATUS_FAILED && this.props.isModerator) {
      return LIST_ROW_COLOR_ERROR;
    }
    return "";
  };

  renderLeftColumn = ({
    workflowInstanceDescription,
    creationTimeUtc,
    createdBy,
  }: Workflow) => {
    return (
      <span>
        <ListItemText
          title={
            <span className={workflowTitleStyle}>
              {workflowInstanceDescription}
            </span>
          }
          description={
            <span className={workflowDescriptionStyle}>
              Created:{" "}
              {moment(new Date(`${creationTimeUtc}Z`)).format(
                "DD MMM YYYY, HH:mm",
              )}
              <span className={workflowCreatedByStyle}>{createdBy}</span>
            </span>
          }
        />
      </span>
    );
  };

  renderRightColumn = (workflow: Workflow) => {
    const { status, csStatus } = workflow;
    const { isModerator } = this.props;

    switch (csStatus) {
      case CS_STATUS_PUBLISHED:
        return render("Published", "ic-check.svg", Mint);
      case CS_STATUS_PUBLISHED_MESSAGE_SENT:
        return render("Published. Notification Sent", "ic-check-all.svg", Mint);
    }

    switch (status) {
      case WORKFLOW_STATUS_WAITING:
        return render("Waiting", "ic-awaiting.svg");
      case WORKFLOW_STATUS_RUNNING:
        return render("In progress", "ic-awaiting.svg");
      case WORKFLOW_STATUS_SUCCESSFUL:
        return render("Done", "ic-check.svg", Mint);
      case WORKFLOW_STATUS_WAITING_DATA:
        return render("Enter to continue", "ic-action.svg");
      case WORKFLOW_STATUS_FAILED:
        if (isModerator) {
          return render("Something went wrong", "ic-alert.svg");
        } else {
          return render("In progress", "ic-awaiting.svg");
        }
      default:
        // note: empty span is used to fix type issues
        return <span />;
    }

    function render(text: string, icon: string, color?: string) {
      let textColor = "rgb(117,117,117)";
      if (status === WORKFLOW_STATUS_FAILED && isModerator) {
        textColor = Blood;
      }
      if (status === WORKFLOW_STATUS_WAITING_DATA) {
        textColor = Gold;
      }

      return (
        <div
          className={style({
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            lineHeight: "24px",
          })}
        >
          <div
            className={style({
              fontSize: "14px",
              color: textColor,
            })}
          >
            {text}
          </div>
          <span className={workflowIconWrapperStyle(color)}>
            <img className={workflowIconStyle} src={icon} />
          </span>
        </div>
      );
    }
  };
}

const workflowTitleStyle = classes(
  regularStyle,
  style({
    fontSize: "20px",
    lineHeight: "26px",
    marginBottom: "2px",
    display: "block",
    letterSpacing: 0,
  }),
);

const workflowDescriptionStyle = classes(
  regularStyle,
  style({
    fontSize: "14px",
    lineHeight: "18px",
    letterSpacing: 0,
  }),
);

const workflowCreatedByStyle = style({
  marginLeft: "18px",
});

const workflowIconWrapperStyle = (color?: string) =>
  style({
    marginLeft: "12px",
    borderRadius: "8px",
    background: color,
  });

const workflowIconStyle = style({ display: "block" });
